import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  LithophaneParams,
  LithophaneShape,
  processImageToHeightMap,
  generateDefaultHeightMap,
  generateLithophaneGeometry,
  exportLithophaneToSTL
} from '@/lib/lithophaneGenerator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Download,
  RotateCcw,
  Box,
  Layers,
  Sliders,
  Upload,
  Lightbulb,
  RotateCw,
  Crosshair
} from 'lucide-react';
import { toast } from 'sonner';

const defaultParams: LithophaneParams = {
  shape: 'curved',
  width: 100,
  height: 130,
  minThickness: 0.8,
  maxThickness: 3.0,
  frameWidth: 3.0,
  frameThickness: 3.8,
  curveAngle: 120,
  hasStandBase: false,
  standDepth: 35,
  inverted: false
};

export default function LithophanePage() {
  const mountRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [params, setParams] = useState<LithophaneParams>(defaultParams);
  const [wireframe, setWireframe] = useState(false);
  const [backlight, setBacklight] = useState(true); // Luz trasera encendida por defecto
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [autoRotate, setAutoRotate] = useState(false);

  // Heightmap en memoria
  const heightMapRef = useRef<Float32Array>(generateDefaultHeightMap(100, 130));

  // Three.js refs
  const sceneRef = useRef<THREE.Scene | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const geomRef = useRef<THREE.BufferGeometry | null>(null);
  const backLightRef = useRef<THREE.PointLight | null>(null);

  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = autoRotate;
    }
  }, [autoRotate]);

  // Inicializar Three.js
  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const width = container.clientWidth;
    const height = container.clientHeight || 560;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x181716); // Fondo oscuro para resaltar la retroiluminación
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
    camera.position.set(0, 70, 220);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    rendererRef.current = renderer;

    container.appendChild(renderer.domElement);

    // Iluminación frontal suave
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // Luz trasera que traspasa la litofanía
    const backLight = new THREE.PointLight(0xffecd2, 4.5, 400);
    backLight.position.set(0, 65, -80);
    scene.add(backLight);
    backLightRef.current = backLight;

    // Grilla
    const grid = new THREE.GridHelper(260, 26, 0x333333, 0x222222);
    grid.position.y = -0.5;
    scene.add(grid);

    // Geometría Litofanía inicial
    const geom = generateLithophaneGeometry(params, heightMapRef.current, 100, 130);
    geomRef.current = geom;

    const mat = new THREE.MeshStandardMaterial({
      color: 0xFDFBF7, // PLA Blanco Cálido
      roughness: 0.5,
      metalness: 0.02,
      side: THREE.DoubleSide,
      wireframe
    });

    const mesh = new THREE.Mesh(geom, mat);
    meshRef.current = mesh;
    scene.add(mesh);

    // Controles orbitales de cámara
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, params.height * 0.45, 0);
    controls.autoRotate = autoRotate;
    controls.autoRotateSpeed = 2.0;
    controls.maxDistance = 500;
    controls.minDistance = 50;
    controlsRef.current = controls;

    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!container || !rendererRef.current) return;
      const nw = container.clientWidth;
      const nh = container.clientHeight || 560;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      rendererRef.current.setSize(nw, nh);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      renderer.dispose();
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, []);

  const handleRecenter = () => {
    if (!cameraRef.current || !controlsRef.current) return;
    controlsRef.current.target.set(0, params.height * 0.45, 0);
    cameraRef.current.position.set(0, 70, 220);
    controlsRef.current.update();
    toast.info('Vista centrada');
  };

  // Actualizar geometría cuando cambian los parámetros
  useEffect(() => {
    if (!meshRef.current || !sceneRef.current) return;

    const newGeom = generateLithophaneGeometry(params, heightMapRef.current, 100, 130);
    geomRef.current = newGeom;

    meshRef.current.geometry.dispose();
    meshRef.current.geometry = newGeom;

    // Actualizar posición de la luz trasera
    if (backLightRef.current) {
      backLightRef.current.position.y = params.height * 0.5;
    }
  }, [params]);

  // Actualizar efectos de retroiluminación y alambre
  useEffect(() => {
    if (!meshRef.current || !sceneRef.current) return;
    const mat = meshRef.current.material as THREE.MeshStandardMaterial;
    mat.wireframe = wireframe;

    if (backlight) {
      sceneRef.current.background = new THREE.Color(0x181716);
      if (backLightRef.current) backLightRef.current.intensity = 4.5;
      mat.emissive.set(0x33281c); // Efecto traslúcido suave
    } else {
      sceneRef.current.background = new THREE.Color(0xF9F8F6);
      if (backLightRef.current) backLightRef.current.intensity = 0.5;
      mat.emissive.set(0x000000);
    }
  }, [backlight, wireframe]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = event => {
      const img = new Image();
      img.onload = () => {
        // Calcular aspect ratio de la imagen
        const aspect = img.width / img.height;
        const newWidth = Math.round(params.height * aspect);

        // Procesar mapa de alturas en Canvas
        const hMap = processImageToHeightMap(img, 100, 130, params.inverted);
        heightMapRef.current = hMap;

        setParams(prev => ({
          ...prev,
          width: Math.min(180, Math.max(60, newWidth))
        }));

        setImageLoaded(true);
        setImagePreview(img.src);
        toast.success('¡Fotografía procesada con éxito!');
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleExportSTL = () => {
    if (!geomRef.current) return;
    toast.success('Exportando litofanía a STL...');
    exportLithophaneToSTL(geomRef.current, `litofania-${params.shape}-${params.width}x${params.height}mm.stl`);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Header */}
      <div className="space-y-3">
        <Link
          to="/tools"
          className="inline-flex items-center text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Volver al Hub de Herramientas
        </Link>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Generador de Litofanías 3D</h1>
              <Badge className="bg-primary/10 text-primary border-primary/20">Fotos Retroiluminadas</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Transformá cualquier fotografía en un modelo 3D traslúcido que revela la imagen al encender la luz.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setParams(defaultParams)}
              className="gap-1.5"
            >
              <RotateCcw className="h-4 w-4" /> Reset
            </Button>
            <Button
              size="sm"
              onClick={handleExportSTL}
              className="gap-1.5 bg-primary text-primary-foreground shadow-sm"
            >
              <Download className="h-4 w-4" /> Exportar STL
            </Button>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* 3D Viewport */}
        <div className="lg:col-span-7 flex flex-col gap-3">
          <Card className={`overflow-hidden border border-border/80 shadow-sm relative flex flex-col min-h-[520px] lg:min-h-[640px] transition-colors ${
            backlight ? 'bg-[#181716]' : 'bg-[#F9F8F6]'
          }`}>
            {/* Controles flotantes */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={backlight ? "default" : "secondary"}
                  className={`text-xs gap-1.5 shadow-sm h-8 ${
                    backlight ? 'bg-amber-500 hover:bg-amber-600 text-black font-semibold' : ''
                  }`}
                  onClick={() => setBacklight(!backlight)}
                >
                  <Lightbulb className={`h-3.5 w-3.5 ${backlight ? 'fill-black text-black' : ''}`} />
                  {backlight ? 'Retroiluminación ON' : 'Encender Luz'}
                </Button>

                <Button
                  size="sm"
                  variant={autoRotate ? "default" : "secondary"}
                  className={`text-xs gap-1.5 shadow-sm h-8 ${
                    autoRotate ? 'bg-primary text-primary-foreground' : ''
                  }`}
                  onClick={() => setAutoRotate(!autoRotate)}
                  title="Giro automático 360°"
                >
                  <RotateCw className={`h-3.5 w-3.5 ${autoRotate ? 'animate-spin' : ''}`} />
                  {autoRotate ? 'Giro ON' : 'Giro Auto'}
                </Button>

                <Button
                  size="sm"
                  variant="secondary"
                  className="text-xs gap-1.5 shadow-sm h-8"
                  onClick={handleRecenter}
                  title="Centrar y reajustar vista"
                >
                  <Crosshair className="h-3.5 w-3.5" /> Centrar
                </Button>

                <Button
                  size="sm"
                  variant={wireframe ? "default" : "secondary"}
                  className="text-xs gap-1 shadow-sm h-8"
                  onClick={() => setWireframe(!wireframe)}
                >
                  <Layers className="h-3.5 w-3.5" /> Malla
                </Button>
              </div>

              {/* Botón Subir Foto */}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png, image/jpeg, image/webp"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs gap-1.5 bg-card/90 backdrop-blur-xs h-8 shadow-xs"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5" /> {imageLoaded ? 'Cambiar Foto' : 'Subir Mi Foto'}
                </Button>
              </div>
            </div>

            {/* Canvas Three.js */}
            <div ref={mountRef} className="w-full flex-1 cursor-grab active:cursor-grabbing" />

            {/* Pie informativo */}
            <div className={`p-3 backdrop-blur-xs border-t text-xs flex items-center justify-between ${
              backlight ? 'bg-black/60 border-neutral-800 text-neutral-400' : 'bg-card/80 border-border/60 text-muted-foreground'
            }`}>
              <span className="flex items-center gap-1.5">
                <Box className="h-3.5 w-3.5 text-primary" />
                Rotar: Arrastre • Desplazar: Clic derecho • Zoom: Rueda
              </span>
              <div className="flex items-center gap-3">
                <span>Forma: <strong className="text-foreground">{params.shape === 'curved' ? 'Curva (Auto-soporte)' : params.shape === 'flat' ? 'Plana' : 'Cilíndrica'}</strong></span>
                <span>Grosor: <strong className="text-foreground">{params.minThickness}mm a {params.maxThickness}mm</strong></span>
              </div>
            </div>
          </Card>
        </div>

        {/* Panel de Controles Paramétricos */}
        <div className="lg:col-span-5 space-y-4">
          {/* Mini preview de la imagen */}
          {imagePreview && (
            <Card className="border border-border/80 shadow-xs p-3 flex items-center gap-3">
              <img
                src={imagePreview}
                alt="Vista previa"
                className="w-14 h-14 object-cover rounded-lg border border-border"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">Foto Cargada</p>
                <p className="text-[11px] text-muted-foreground">Procesada con mapa de alturas luma</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => fileInputRef.current?.click()}
              >
                Cambiar
              </Button>
            </Card>
          )}

          <Card className="border border-border/80 shadow-xs">
            <CardHeader className="pb-3 border-b border-border/50">
              <CardTitle className="text-base flex items-center gap-2">
                <Sliders className="h-4 w-4 text-primary" /> Geometría & Relieve
              </CardTitle>
            </CardHeader>

            <CardContent className="pt-4 space-y-4">
              {/* Selector de Forma */}
              <div>
                <Label className="text-xs mb-1.5 block">Formato de Litofanía</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { id: 'curved', label: 'Curva (120°)' },
                    { id: 'flat', label: 'Plana con Marco' },
                    { id: 'cylinder', label: 'Cilindro 360°' }
                  ].map(sh => (
                    <Button
                      key={sh.id}
                      size="sm"
                      variant={params.shape === sh.id ? "default" : "outline"}
                      className="text-xs h-8 px-1 justify-center"
                      onClick={() => setParams({ ...params, shape: sh.id as LithophaneShape })}
                    >
                      {sh.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Medidas */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <Label>Ancho (X)</Label>
                    <span className="font-semibold text-primary">{params.width} mm</span>
                  </div>
                  <input
                    type="range"
                    min="60"
                    max="180"
                    value={params.width}
                    onChange={e => setParams({ ...params, width: Number(e.target.value) })}
                    className="w-full accent-primary cursor-pointer"
                  />
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <Label>Altura (Y)</Label>
                    <span className="font-semibold text-primary">{params.height} mm</span>
                  </div>
                  <input
                    type="range"
                    min="70"
                    max="200"
                    value={params.height}
                    onChange={e => setParams({ ...params, height: Number(e.target.value) })}
                    className="w-full accent-primary cursor-pointer"
                  />
                </div>
              </div>

              {/* Espesores (Luz y Sombra) */}
              <div className="p-3 bg-muted/40 rounded-xl space-y-3 border border-border/50">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <Label>Espesor Mínimo (Zonas Claras)</Label>
                    <span className="font-semibold">{params.minThickness} mm</span>
                  </div>
                  <input
                    type="range"
                    min="0.6"
                    max="1.4"
                    step="0.1"
                    value={params.minThickness}
                    onChange={e => setParams({ ...params, minThickness: Number(e.target.value) })}
                    className="w-full accent-primary cursor-pointer"
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    0.8mm permite que la luz traspase nítidamente el filamento blanco.
                  </p>
                </div>

                <div className="pt-2 border-t border-border/40">
                  <div className="flex justify-between text-xs mb-1">
                    <Label>Espesor Máximo (Zonas Oscuras)</Label>
                    <span className="font-semibold">{params.maxThickness} mm</span>
                  </div>
                  <input
                    type="range"
                    min="2.2"
                    max="4.5"
                    step="0.1"
                    value={params.maxThickness}
                    onChange={e => setParams({ ...params, maxThickness: Number(e.target.value) })}
                    className="w-full accent-primary cursor-pointer"
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Mayor espesor bloquea más luz creando contrastes profundos.
                  </p>
                </div>
              </div>

              {/* Opciones de Base / Soporte */}
              {params.shape !== 'cylinder' && (
                <div className="p-3 bg-muted/40 rounded-xl space-y-2 border border-border/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs font-semibold">Base de Soporte para Mesa</Label>
                      <p className="text-[11px] text-muted-foreground">Pie integrado para apoyar sobre escritorio</p>
                    </div>
                    <input
                      type="checkbox"
                      className="rounded h-4 w-4 cursor-pointer accent-primary"
                      checked={params.hasStandBase}
                      onChange={e => setParams({ ...params, hasStandBase: e.target.checked })}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
